import Yup from '../../../utils/yup';

const dash = () =>
  Yup.string()
    .nullable()
    .transform((value) =>
      value === null || value === undefined || value === ''
        ? '-'
        : String(value),
    )
    .default('-');

const nullableNumber = () => Yup.number().nullable().default(null);
const nullableString = () => Yup.string().nullable().default(null);
const nullableNumberOrString = () =>
  Yup.mixed<number | string>()
    .nullable()
    .transform((value) =>
      typeof value === 'number' || typeof value === 'string' ? value : null,
    )
    .default(null);

const pessoaSchema = Yup.object({
  id: nullableNumber(),
  nome: dash(),
});

const glebaSchema = Yup.object({
  id: nullableNumber(),
  descricao: dash(),
  kml: nullableString(),
});

const campoSchema = Yup.object({
  label: dash(),
  value: Yup.mixed<string | number | string[]>().nullable().default(null),
  type: Yup.string().nullable().default('text'),
  cor: nullableString(),
});

const linhaCultivarSchema = Yup.object({
  ordem: Yup.number().nullable().default(0),
  campos: Yup.array().of(campoSchema).default([]),
});

const ndviLegendaSchema = Yup.object({
  ordem: Yup.number().nullable().default(0),
  texto: dash(),
  cor: nullableString(),
});

const ndviSchema = Yup.object({
  imagem: nullableString(),
  data: nullableString(),
  legenda: Yup.array().of(ndviLegendaSchema).default([]),
})
  .nullable()
  .default(null);

const cabecalhoSchema = Yup.object({
  descricao: dash(),
  areaHa: nullableNumber(),
  ndvi: ndviSchema,
  linhas: Yup.array().of(linhaCultivarSchema).default([]),
});

const resumoCelulaSchema = Yup.object({
  ordem: Yup.number().nullable().default(0),
  valor: dash(),
  cor: nullableString(),
});

const resumoValorSchema = Yup.object({
  ordem: Yup.number().nullable().default(0),
  linha: Yup.array().of(resumoCelulaSchema).default([]),
});

const resumoTabelaSchema = Yup.object({
  ordem: Yup.number().nullable().default(0),
  titulo: dash(),
  icone: nullableString(),
  mapaInfestacao: nullableString(),
  colunas: Yup.array().of(Yup.string().default('-')).default([]),
  valores: Yup.array().of(resumoValorSchema).default([]),
});

const imagemSchema = Yup.object({
  url: nullableString(),
});

const marcadorSchema = Yup.object({
  tipo: nullableString(),
  classificacao: nullableString(),
  situacao: nullableString(),
})
  .nullable()
  .default(null);

const pontoSchema = Yup.object({
  ordem: Yup.number().nullable().default(0),
  tipo: dash(),
  latitude: nullableNumber(),
  longitude: nullableNumber(),
  observacao: dash(),
  marcador: marcadorSchema,
  imagens: Yup.array().of(imagemSchema).default([]),
  campos: Yup.array().of(campoSchema).default([]),
  resumo: Yup.array().of(resumoTabelaSchema).nullable().default([]),
});

const resumoIndicadorValorSchema = Yup.object({
  valor: dash(),
  cor: nullableString(),
  bold: Yup.boolean().nullable().default(false),
});

const resumoIndicadorSchema = Yup.object({
  ordem: Yup.number().nullable().default(0),
  label: dash(),
  valores: Yup.array().of(resumoIndicadorValorSchema).default([]),
});

const resumoOcorrenciaCardSchema = Yup.object({
  ordem: Yup.number().nullable().default(0),
  titulo: dash(),
  mapaInfestacao: nullableString(),
  linhas: Yup.array().of(resumoIndicadorSchema).default([]),
});

const resumoOcorrenciasSchema = Yup.object({
  titulo: dash(),
  qtdPontos: nullableNumber(),
  icone: nullableString(),
  cards: Yup.array().of(resumoOcorrenciaCardSchema).default([]),
})
  .nullable()
  .default(null);

const grupoSchema = Yup.object({
  cabecalho: cabecalhoSchema,
  pontos: Yup.array().of(pontoSchema).default([]),
});

const talhaoSchema = Yup.object({
  gleba: glebaSchema,
  grupo: Yup.array().of(grupoSchema).default([]),
  resumoOcorrencias: resumoOcorrenciasSchema,
});

const produtoSchema = Yup.object({
  descricao: dash(),
  dose: nullableNumberOrString(),
  unidade: dash(),
});

const recomendacaoGlebaSchema = Yup.object({
  id: nullableNumber(),
  descricao: dash(),
  areaHa: nullableNumber(),
});

const recomendacaoSchema = Yup.object({
  gleba: recomendacaoGlebaSchema,
  id: nullableNumber(),
  atividade: dash(),
  periodo: dash(),
  recomendante: dash(),
  vazao: nullableNumber(),
  bico: dash(),
  dataInicio: dash(),
  dataFim: dash(),
  produtos: Yup.array().of(produtoSchema).default([]),
});

const pluviometroSchema = Yup.object({
  id: nullableNumber(),
  descricao: dash(),
  totalMm: nullableNumber(),
});

const chuvaAcumuladaSchema = Yup.object({
  data: dash(),
  mediaGeralMm: nullableNumber(),
  porPluviometro: Yup.array().of(pluviometroSchema).default([]),
})
  .nullable()
  .default(null);

const headerSchema = Yup.object({
  propriedade: Yup.object({
    id: nullableNumber(),
    descricao: dash(),
    produtor: pessoaSchema,
  }),
  safra: Yup.object({
    id: nullableNumber(),
    descricao: dash(),
    dataInicial: dash(),
    dataFinal: dash(),
  }),
  dataVisita: dash(),
  duracao: dash(),
  responsavel: pessoaSchema,
  monitor: Yup.array().of(pessoaSchema).default([]),
  situacaoGeral: dash(),
  proximaVisita: dash(),
  logomarca: nullableString(),
});

export const relatorioVisitaSchema = Yup.object({
  id: nullableNumber(),
  meta: Yup.object({
    geradoEm: dash(),
    geradoPor: pessoaSchema,
  }).default(undefined),
  header: headerSchema.required(),
  talhoes: Yup.array().of(talhaoSchema).default([]),
  recomendacoes: Yup.array().of(recomendacaoSchema).default([]),
  chuvaAcumulada: chuvaAcumuladaSchema,
});

export type RelatorioVisita = Yup.InferType<typeof relatorioVisitaSchema>;
export type RelatorioVisitaTalhao = Yup.InferType<typeof talhaoSchema>;
export type RelatorioVisitaGrupo = Yup.InferType<typeof grupoSchema>;
export type RelatorioVisitaCabecalho = Yup.InferType<typeof cabecalhoSchema>;
export type RelatorioVisitaPonto = Yup.InferType<typeof pontoSchema>;
export type RelatorioVisitaMarcador = Yup.InferType<typeof marcadorSchema>;
export type RelatorioVisitaCampo = Yup.InferType<typeof campoSchema>;
export type RelatorioVisitaResumo = Yup.InferType<typeof resumoTabelaSchema>;
export type RelatorioVisitaResumoOcorrencias = Yup.InferType<
  typeof resumoOcorrenciasSchema
>;
export type RelatorioVisitaRecomendacao = Yup.InferType<
  typeof recomendacaoSchema
>;
export type RelatorioVisitaChuva = Yup.InferType<typeof chuvaAcumuladaSchema>;
